/** @jsx jsx */
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import SidebarTemplate from 'components/templates/SidebarTemplate';
import FormContents from 'components/atoms/Form/FormContents';
import {
  Container, Row, Col,
} from 'react-grid-system';
import moment from 'moment';
import BodyText from 'components/atoms/BodyText';
import Button, { PrimaryButton, DeleteButton } from 'components/atoms/Button';
import ConfirmModal from 'components/organismos/ConfirmModal';
import TextForm from 'components/molecules/TextForm';
import FormField from 'components/atoms/Form/FormField';
import BlockUI from 'components/molecules/BlockUi';
import FlexBox from 'components/atoms/FlexBox';
import ToastModal from '../../../../organismos/ToastModal';
import { OvertimeApplicationForm } from './hooks';
import Table from 'components/molecules/Table';
import { css, jsx } from '@emotion/core';
import { textColor, grayScale, textFontSize } from 'components/styles';
import AttendHolidayApplicationDomain from 'domain/master/attend/holidayApplication';
import { Column } from 'react-table';
import FormPadding from 'components/atoms/Form/FormPadding';
import SelectForm from 'components/molecules/SelectForm';
import HistoryApprovalTable from 'components/molecules/HistoryApprovalTable';
import TabControl from 'components/atoms/TabControl';
import AttendOvertimeModal from 'components/molecules/ChangeLogModal/Template/AttendOvertimeModal';
import FormHeader from 'components/templates/FormHeader';

const styles = {
  container: {
    marginTop: '30px'
  },
  tableContent: css({
    justifyContent: 'center',
    alignItems: 'center'
  }),
  colorText: css({
    color: grayScale.gray100,
  }),
  formContent: {
    paddingBottom: "20px"
  },
  approvalWrap: css({
    padding: '0px',
    marginBottom: '15px',
    marginTop: '0px'
  }),
};

const OvertimeApplicationPage: React.FC = () => {
  const history = useHistory();
  const {
    formik,
    orgTreesOptions,
    targetDate,
    toastMessage,
    toastModalOpen,
    setToastModalOpen,
    confirmApplyModalOpen,
    closeConfirmApplyModal,
    isLoading,
    dispStaffName,
    setSelectOrgCode,
    isGetOvertionSuccees,
    selectChangelog,
    setSelectChangelog,
    fetchChangeLogOvertime
  } = OvertimeApplicationForm();

  const columns: Array<Column<AttendHolidayApplicationDomain>> = useMemo(() => [
    {
      Header: 'No',
      accessor: 'version',
    },
    {
      Header: '申請種別',
      id: 'modifiyStatus',
      accessor: 'modifiyStatus',
      Cell: (cell: { row: { isExpanded: boolean, original: any }, page: [] }) => {
        if (cell.row.original.modifiyStatus === 0) {
          return '申請';
        }
        if (cell.row.original.modifiyStatus === 1) {
          return '削除';
        }
        if (cell.row.original.modifiyStatus !== 0 && cell.row.original.modifiyStatus !== 1) {
          return '';
        }
      },
    },
    {
      Header: '日付',
      id: 'targetDate',
      accessor: 'targetDate',
      Cell: (cell: { row: { isExpanded: boolean, original: any }, page: [] }) => {
        if (cell.row.original.targetDate != null) {
          return moment(cell.row.original.targetDate).format('YYYY/MM/DD(ddd)');
        }
      },
    },
    {
      Header: '申請残業時間',
      id: 'applicationOvertime',
      accessor: 'applicationOvertime',
      Cell: (cell: { row: { isExpanded: boolean, original: any }, page: [] }) => {
        return cell.row.original.applicationOvertime + "分";
      },
    },
    {
      Header: '申請日時',
      id: 'applicationDate',
      accessor: 'applicationDate',
      Cell: (cell: { row: { isExpanded: boolean, original: any }, page: [] }) => {
        if (cell.row.original.applicationDate != null) {
          return moment(cell.row.original.applicationDate).format('YYYY/MM/DD HH:mm:ss');
        }
        if (cell.row.original.applicationDate == null) {
          return '';
        }
      },
    },
    {
      Header: '申請者',
      id: 'applicationStaffCode',
      accessor: 'applicationStaffCode',
      Cell: (cell: { row: { isExpanded: boolean, original: any }, page: [] }) => {
        return (
          <span>
            <div>{cell.row.original.applicationStaffCode}<br></br>{cell.row.original.applicationStaffName}</div>
          </span>
        );
      },
    },
    {
      Header: '申請理由',
      accessor: 'applicationReason',
    },
    {
      Header: '決裁状況',
      id: 'applicationStatus',
      accessor: 'applicationStatus',
      Cell: (cell: { row: { isExpanded: boolean, original: any }, page: [] }) => {
        if (cell.row.original.applicationStatus === 0) {
          return '申請中';
        }
        if (cell.row.original.applicationStatus === 1) {
          return '承認';
        }
        if (cell.row.original.applicationStatus === 2) {
          return '差戻';
        }
        if (cell.row.original.applicationStatus !== 0 && cell.row.original.applicationStatus !== 1 && cell.row.original.applicationStatus !== 2) {
          return '';
        }
      },
    },
    {
      Header: '決済日時',
      id: 'approvalDate',
      accessor: 'approvalDate',
      Cell: (cell: { row: { isExpanded: boolean, original: any }, page: [] }) => {
        if (cell.row.original.approvalDate != null) {
          return moment(cell.row.original.approvalDate).format('YYYY/MM/DD HH:mm:ss');
        }
        if (cell.row.original.approvalDate == null) {
          return '';
        }
      },
    },
    {
      Header: '差戻コメント',
      accessor: 'rejectReason',
    },
  ], []);

  const sortBy = React.useMemo(
    () => [
      {
        id: 'no',
        desc: false,
      },
    ],
    [],
  );

  const [changeLogData, setChangeLogData] = useState<Array<any>>([]);

  useEffect(() => {
    if (formik.values.orgCode || formik.values.targetDate || formik.values.staffCode) {
      fetchChangeLogOvertime(formik.values.orgCode, formik.values.targetDateStr, formik.values.staffCode).then(res => {
        setChangeLogData(res);
      });
    }
  }, [formik.values.orgCode, formik.values.targetDate, formik.values.staffCode]);
  
  return (
    <SidebarTemplate>
      <BlockUI blocking={isLoading}>
      <FormHeader title="残業申請"/>
        <FormContents customStyle={css({marginTop:'60px'})}>
          <form onSubmit={formik.handleSubmit}>
            <Container fluid={true}>
              <FormPadding>
                <Row style={{ marginBottom: '20px' }}>
                  <Col md={2} xs={12}>
                    <BodyText>日時</BodyText>
                  </Col>
                  <Col md={6} xs={12}>
                    <BodyText customStyle={styles.colorText}>
                      {moment(`${targetDate}`).format('YYYY年MM月DD日(ddd)')}
                    </BodyText>
                  </Col>
                </Row>
                <Row style={{ marginBottom: '20px' }}>
                  <Col md={2} xs={12}>
                    <BodyText>スタッフ</BodyText>
                  </Col>
                  <Col md={6} xs={12}>
                    <BodyText customStyle={styles.colorText}>
                      {dispStaffName}
                    </BodyText>
                  </Col>
                </Row>
                {
                  isGetOvertionSuccees && (
                    <Row style={{ marginBottom: '20px' }}>
                      { formik.values.modifierApplicationId === null && (
                        <React.Fragment>
                          <Col style={{ display: 'flex', alignItems: 'center' }} md={2} xs={12}>
                            <BodyText>出勤組織</BodyText>
                          </Col>
                          <Col xl={5} md={6} xs={12}>
                            <SelectForm
                              label=""
                              name="orgCode"
                              value={String(formik.values.orgCode)}
                              setValue={(val: string) => setSelectOrgCode(val)}
                              options={orgTreesOptions}
                              required={false}
                              ghost={sessionStorage.getItem('loginUser.site') !== '3'}
                              disable={formik.values.applicationStatus === 0 && formik.values.createDate !== null}
                            />
                          </Col>
                        </React.Fragment>
                      )}
                      {
                        formik.values.modifierApplicationId !== null && (
                          <Col xs={12} style={{ marginTop: '10px' }}>
                            <Row>
                              <Col md={2} xs={12}>
                                <BodyText>出勤組織</BodyText>
                              </Col>
                              <Col md={6} xs={12}>
                                <BodyText customStyle={styles.colorText}>
                                  {`${formik.values.orgCode}${formik.values.orgName}`}
                                </BodyText>
                              </Col>
                            </Row>
                          </Col>
                        )
                      }
                    </Row>
                  )
                }
                {
                  sessionStorage.getItem('attendStampList.coreTime') && (
                    <Row style={{ marginBottom: '20px' }}>
                      <Col md={2} xs={12}>
                        <BodyText>勤務予定</BodyText>
                      </Col>
                      <Col md={6} xs={12}>
                        <BodyText customStyle={styles.colorText}>
                          {sessionStorage.getItem('attendStampList.coreTime')}
                        </BodyText>
                      </Col>
                    </Row>
                  )
                }
              </FormPadding>
              <Row className="table-padding">
                <Table customStyle={styles.container}>
                  <thead>
                    <tr>
                      {
                        (formik.values.workTimeData != null && formik.values.workTimeData.workTimeType === 1) && <Table.HeaderCell>出勤時間（予定）</Table.HeaderCell>
                      }
                      {
                        (formik.values.workTimeData != null && formik.values.workTimeData.workTimeType === 1) && <Table.HeaderCell>退勤時間（予定）</Table.HeaderCell>
                      }
                      {
                        (formik.values.workTimeData == null || formik.values.workTimeData.workTimeType !== 1) && <Table.HeaderCell>出勤時間</Table.HeaderCell>
                      }
                      {
                        (formik.values.workTimeData == null || formik.values.workTimeData.workTimeType !== 1) && <Table.HeaderCell>退勤時間</Table.HeaderCell>
                      }
                      <Table.HeaderCell>残業時間</Table.HeaderCell>
                      <Table.HeaderCell>申請残業時間</Table.HeaderCell>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <Table.Cell>
                        <FlexBox customStyle={styles.tableContent}>
                          {(formik.values.workTimeData != null && formik.values.workTimeData.startTime) ? formik.values.workTimeData.startTime : ''}
                        </FlexBox>
                      </Table.Cell>
                      <Table.Cell>
                        <FlexBox customStyle={styles.tableContent}>
                          {(formik.values.workTimeData != null && formik.values.workTimeData.endTime) ? formik.values.workTimeData.endTime : ''}
                        </FlexBox>
                      </Table.Cell>
                      <Table.Cell>
                        <FlexBox customStyle={styles.tableContent}>
                          {(formik.values.workTimeData != null && formik.values.workTimeData.overtime) ? formik.values.workTimeData.overtime : 0}
                            分
                          </FlexBox>
                      </Table.Cell>
                      <Table.Cell>
                        <FlexBox customStyle={styles.tableContent}>
                          <div style={{ maxWidth: '65px' }}>
                            <TextForm
                              name="applicationOvertimeStr"
                              label=""
                              value={formik.values.applicationOvertimeStr}
                              onChange={formik.handleChange}
                              errorMsg={formik.errors.applicationOvertimeStr}
                              disable={formik.values.applicationStatus === 0 && formik.values.createDate !== null}
                            />
                          </div>
                          <span style={{ margin: '0 5px' }}><b>分</b></span>
                        </FlexBox>
                      </Table.Cell>
                    </tr>
                  </tbody>
                </Table>
              </Row>
              <Row>
                <Col md={2} offset={{ md: 1 }} style={{ alignItems: 'center', justifyContent: 'center', display: 'flex', color: textColor.main }}>
                  <BodyText>申請理由</BodyText>
                </Col>
                <Col md={6}>
                  <FormField>
                    <TextForm
                      required={false}
                      name="applicationReason"
                      label=""
                      value={String(formik.values.applicationReason)}
                      onChange={formik.handleChange}
                      errorMsg={formik.errors.applicationReason}
                      disable={formik.values.applicationStatus === 0}
                    />
                  </FormField>
                </Col>
              </Row>
              <Row>
                <Col md={12} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div
                    css={css`
                    .btnAction:nth-of-type(2) {
                      margin-left: 10px;
                    }
                    .btnAction:nth-of-type(3) {
                      margin-left: 10px;
                    }
                  `}>
                    <FlexBox>
                      {
                        (isGetOvertionSuccees && (formik.values.modifiyStatus === null || formik.values.modifiyStatus === 0
                          || (formik.values.modifiyStatus === 1 && formik.values.applicationStatus !== 0))) && (
                          <div className="btnAction">
                            <PrimaryButton
                              ghost={false}
                              disabled={formik.values.applicationStatus === 0 && formik.values.createDate !== null}
                              text="申請する"
                              onClick={() => {
                                formik.handleSubmit();
                                formik.setFieldValue('buttonType', 0);
                              }}
                            />
                          </div>
                        )
                      }
                      {
                        formik.values.applicationStatus === 0 && (
                          <div className="btnAction">
                            <DeleteButton
                              ghost={false}
                              text="申請キャンセル"
                              onClick={() => {
                                formik.handleSubmit();
                                formik.setFieldValue('buttonType', 1);
                              }}
                            />
                          </div>
                        )
                      }
                      {
                        formik.values.deleteAvailable === true && (
                          <div className="btnAction">
                            <DeleteButton
                              ghost={false}
                              text="削除申請"
                              onClick={() => {
                                formik.handleSubmit();
                                formik.setFieldValue('buttonType', 2);
                              }}
                            />
                          </div>
                        )
                      }
                    </FlexBox>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={12} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '30px' }}>
                  <FlexBox>
                    <Button
                      onClick={() => history.goBack()}
                      text="キャンセル"
                      ghost={true}
                    />
                  </FlexBox>
                </Col>
              </Row>
            </Container>
          </form>
          <ToastModal
            open={toastModalOpen}
            closeHandler={() => setToastModalOpen(false)}
            title={toastMessage}
          />
          <ConfirmModal
            open={confirmApplyModalOpen}
            closeHandler={closeConfirmApplyModal}
            onSubmit={formik.handleSubmit}
            // eslint-disable-next-line no-nested-ternary
            title={formik.values.buttonType === 0 ? (formik.values.modifiyStatus === 0 ? '残業修正を申請' : '残業を申請')
              : formik.values.buttonType === 1 ? '残業キャンセルを申請' : '削除残業を申請'}
            // eslint-disable-next-line no-nested-ternary
            content={formik.values.buttonType === 0 ? (formik.values.modifiyStatus === 0 ? '残業修正を申請します。よろしいですか？' : '残業を申請します。よろしいですか？')
              : formik.values.buttonType === 1 ? '残業キャンセルを申請します。よろしいですか？' : '削除残業を申請します。よろしいですか？'}
            // submitText={formik.values.buttonType === 0 ? '申請'
            //   : formik.values.buttonType === 1 ? '申請キャンセル' : '削除申請'}
            submitText='申請する'
            typeSubmit={formik.values.buttonType === 0 ? 'confirm' : 'reject'}
          />
        </FormContents>
      </BlockUI>

      <div style={{ paddingTop: 20 }}>
        <FormContents customStyle={styles.formContent}>
          <Row style={{ padding: "10px 30px" }}>
            <Col
              md={2}
              xs={12}
              style={{
                fontWeight: "bold",
                fontSize: textFontSize.md,
                lineHeight: "24px",
                paddingBottom: "20px",
                color: textColor.main
              }}>
              更新履歴
                        </Col>
          </Row>
          <TabControl
            isTabModal={true}
            isFixed={false}
            items={changeLogData.map((log, index: number) => {
              return {
                value: String(index),
                display: `ver${log.version}`
              };
            })}
            setValue={setSelectChangelog as (arg: string) => void}
            currentValue={selectChangelog}
            styleNotActive={css({ borderRadius: 0, border: "none", width: "100px", flex: "unset" })}
            styleActive={css({
              borderRadius: 0,
              border: "none",
              borderBottom: "1px solid #007BC3",
              width: "100px",
              flex: "unset"
            })}
          />
          {changeLogData.length > 0 &&
            changeLogData.map((itemLog, index: number) => (
              <div style={{ display: selectChangelog !== String(index) ? "none" : "block" }}>
                <React.Fragment>
                  <FormPadding customStyle={{ fontSize: "14px", color: textColor.mobileTitle }}>
                    <Row>
                      <Col
                        md={2}
                        xs={12}
                        style={{
                          fontWeight: "bold",
                          fontSize: textFontSize.re,
                          lineHeight: "24px",
                          paddingBottom: "20px",
                          color: textColor.main
                        }}>
                        申請種別:
                                            </Col>
                      <Col md={10} xs={12}>
                        {itemLog.modifiyStatus == 0 ? "申請" : "削除"}
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        md={2}
                        xs={12}
                        style={{
                          fontWeight: "bold",
                          fontSize: textFontSize.re,
                          lineHeight: "24px",
                          paddingBottom: "20px",
                          color: textColor.main
                        }}>
                        申請者:
                                            </Col>
                      <Col md={10} xs={12}>
                        {itemLog.applicationStaffCode && `${itemLog.applicationStaffCode} `}
                        {itemLog.applicationStaffName && itemLog.applicationStaffName}
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        md={2}
                        xs={12}
                        style={{
                          fontWeight: "bold",
                          fontSize: textFontSize.re,
                          lineHeight: "24px",
                          paddingBottom: "20px",
                          color: textColor.main
                        }}>
                        申請日時:
                                            </Col>
                      <Col md={10} xs={12}>
                        {itemLog.applicationDate && moment(itemLog.applicationDate).format("YYYY/MM/DD HH:mm:ss")}
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        md={2}
                        xs={12}
                        style={{
                          fontWeight: "bold",
                          fontSize: textFontSize.re,
                          lineHeight: "24px",
                          paddingBottom: "10px",
                          color: textColor.main
                        }}>
                        出勤組織:</Col>
                      <Col md={10} xs={12}>
                        {itemLog.orgCode && itemLog.orgCode}
                        {itemLog.orgName && itemLog.orgName}
                      </Col>
                    </Row>
                    <Row>
                      <AttendOvertimeModal
                        changeLogData={[itemLog]}
                        selectChangelog={String(index)}
                        customStyle={styles.approvalWrap}
                      />
                    </Row>
                    <Row>
                      <Col
                        md={2}
                        xs={12}
                        style={{
                          fontWeight: "bold",
                          fontSize: textFontSize.re,
                          lineHeight: "24px",
                          paddingBottom: "20px",
                          color: textColor.main
                        }}>
                        修正理由: </Col>
                      <Col md={10} xs={12}>
                        {itemLog.applicationReason && itemLog.applicationReason}
                      </Col>
                    </Row>
                    {changeLogData.length > 0 && (
                      <Row>
                        <HistoryApprovalTable
                          historyData={itemLog.approvalHistoryList}
                          applicationStatus={itemLog.applicationStatus}
                        />
                      </Row>
                    )}
                  </FormPadding>
                </React.Fragment>
              </div>
            ))}
        </FormContents>
      </div>
    </SidebarTemplate>
  );
};

export default OvertimeApplicationPage;
